import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { formValueSelector } from 'redux-form';

import { actionCreators } from 'react/api/actions/routeDivisionModal';
import DateField from 'react/components/fields/DateField';
import LookupField from 'react/components/fields/LookupField';
import RadioGroupField from 'react/components/fields/RadioGroupField';
import TextField from 'react/components/fields/TextField';
import CheckboxField from 'react/components/fields/CheckboxField';
import {
  BaseCrud,
  FormSection,
  FormSectionColumn
} from 'react/containers/layout/Crud';


const beforeSubmit = (values) => {
  const currentDate = Monet.Format.DateTime.getTodayISOString();
  const {
    origin
  } = values;

  const { destinationOption, equipmentAssignments, driverAssignments, ...filteredValues } = values;
  const destinationShouldBeEqualOrigin = destinationOption === 'equalToOrigin';

  const parsedEquipmentAssignments = equipmentAssignments?.map(
    (equipment, index) => ({
      equipment: {
        id: equipment.id
      },
      principal: index === 0
    })
  );
  const parseDriverAssignments = driverAssignments?.map((driver) => ({
    driver: {
      id: driver.id
    }
  }));

  const plannedValues = {
    plannedArrival: currentDate,
    plannedComplete: currentDate,
    plannedDeparture: currentDate,
    plannedStart: currentDate
  };

  if (destinationShouldBeEqualOrigin) {
    Object.assign(filteredValues, { destination: origin });
  }

  const updatedValues = {
    ...plannedValues,
    ...filteredValues
  };

  if (parsedEquipmentAssignments) {
    Object.assign(updatedValues, {
      equipmentAssignments: parsedEquipmentAssignments
    });
  }

  if (parseDriverAssignments) {
    Object.assign(updatedValues, { driverAssignments: parseDriverAssignments });
  }

  Object.assign(updatedValues, { hasHelper: false });

  return updatedValues;
};

const RouteDivisionForm = (props) => {
  const {
    destinationOption,
    destination,
    organization,
    routeValidate,
  } = props;

  const shouldChooseDestination = destinationOption === 'chooseDestination';
  const destinationShouldBeTheLastStop = destinationOption === 'lastStop';

  const closeAction = () => {
    const { closeModalAction } = props;

    closeModalAction();
  };

  const getDestinationOption = () => {

    if (destinationOption) {
      return destinationOption;
    }

    return undefined;
  }

  const renderGeneralSection = () => (
    <FormSection title="route.section.generalInformation">
      <FormSectionColumn>
        <TextField
          description="key"
          label="route.key"
          name="key"
          required
        />
        <DateField
          description="date"
          label="route.date"
          name="date"
          type="DATE"
          required
        />
        <LookupField
          label="route.originLocationID"
          name="origin"
          lookupEntity="Location"
          lookupEntityColumn="description"
          required
        />
        <RadioGroupField
          id="destinationOption"
          name="destinationOption"
          label="route.destionationLocationID"
          defaultValue="lastStop"
          required
          options={[
            {
              label: 'Route.field.destination.lastStop',
              value: 'lastStop'
            },
            {
              label: 'Route.field.destination.equalToOrigin',
              value: 'equalToOrigin'
            },
            {
              label: 'Route.field.destination.chooseDestination',
              value: 'chooseDestination'
            }
          ]}
          value={getDestinationOption()}
        />
        <LookupField
          label="route.destionationLocationID"
          name="destination"
          lookupEntity="Location"
          lookupEntityColumn="description"
          value={shouldChooseDestination ? destination : ''}
          disabled={!shouldChooseDestination}
          required={shouldChooseDestination}
        />
      </FormSectionColumn>
      <FormSectionColumn>
        <LookupField
          label="location.organization"
          name="organization"
          lookupEntity="Organization"
          lookupEntityColumn="description"
          required
        />
        <TextField
          label="route.description"
          name="description"
          maxLength={45}
        />
        <CheckboxField
          name="hasHelper"
          label="route.hasHelper"
          value={false}
          shouldShow={false}
        />
        <CheckboxField
          name="lastStopIsDestination"
          label="route.lastStopIsDestination"
          shouldShow={false}
          value={destinationShouldBeTheLastStop}
        />
      </FormSectionColumn>
    </FormSection>
  )

  const renderAssignmentSection = () => (
    <FormSection title="Assignments">
      <FormSectionColumn>
        <LookupField
          label="Route.AssingDriver.label.driver"
          name="driverAssignments"
          lookupEntity="Driver"
          lookupEntityColumn="name"
          disabled={!organization}
          customCriteriaChain={
            [
              {
                attr: "organization.id",
                eq: organization?.id
              }
            ]
          }
          multiple
        />
      </FormSectionColumn>
      <FormSectionColumn>
        <LookupField
          label="Equipment"
          name="equipmentAssignments"
          lookupEntity="Equipment"
          lookupEntityColumn="key"
          disabled={!organization}
          customCriteriaChain={
            [
              {
                attr: "organization.id",
                eq: organization?.id
              }
            ]
          }
          multiple
        />
      </FormSectionColumn>
    </FormSection>
  );

  return (
    <BaseCrud
      tabName="Route"
      entity="Route"
      updateMethod="PATCH"
      saveButtonLabel="splitRouteModal.button.continue"
      cancelButtonLabel="route.button.cancel"
      customCloseAction={closeAction}
      beforeSubmit={beforeSubmit}
      customSubmitAction={({ data }) => routeValidate(data)}
      keepDataOnUnmount
      {...props}
    >
      {renderGeneralSection()}
      {renderAssignmentSection()}
    </BaseCrud>
  );
};

RouteDivisionForm.defaultProps = {
  destination: null,
  destinationOption: 'lastStop'
};

RouteDivisionForm.propTypes = {
  closeFormAction: PropTypes.func.isRequired,
  closeModalAction: PropTypes.func.isRequired,
  destinationOption: PropTypes.string,
  destination: PropTypes.object,
  isLoading: PropTypes.bool.isRequired
};

const valueSelector = formValueSelector('Route');

const mapStateToProps = (state) => ({
  destinationOption: valueSelector(state, 'destinationOption'),
  destination: valueSelector(state, 'destionationLocationID'),
  organization: valueSelector(state, 'organization'),
  isLoading: state.routeDivisionModal.get('loading')
});

const mapDispatchToProps = {
  closeModalAction: actionCreators.closeModal,
  closeFormAction: actionCreators.closeForm,
  openFormAction: actionCreators.openForm,
  backToFormAction: actionCreators.backToForm,
  routeValidate: actionCreators.routeValidate,
  toggleLoading: actionCreators.toggleLoading,
};

export default connect(mapStateToProps, mapDispatchToProps)(RouteDivisionForm);
